import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { CatalogController } from "../controllers/catalog";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { useSiteMode } from "../context/SiteModeContext";
import { useOrderDraft } from "../context/OrderDraftContext";
import { WAREHOUSES, mockAvailability } from "../lib/mockInventory";
import { variantSku } from "../lib/variant";
import { applyFilters, applySort, deriveFacets } from "../lib/itemFacets";
import { search as searchCatalog } from "../lib/searchIndex";
import SortFilterBar from "../components/SortFilterBar";
import ItemFilters from "../components/ItemFilters";
import { SORT_OPTIONS } from "../components/ItemListToolbar";
import OrderSheetItem from "../components/OrderSheetItem";
import styles from "./OrderSheet.module.css";

// B2B order sheet — flat catalog rendered as a fast-entry quantity grid
// keyed by variant × warehouse. Submit hands off to OrderReview.
export default function OrderSheet() {
  const { currency } = useCurrency();
  const { user } = useAuth();
  const { effectiveMode } = useSiteMode();
  const { lines, totalQty, totalAmount, clear } = useOrderDraft();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState("name_asc");
  const [q, setQ] = useState("");
  const [includeOutOfStock, setIncludeOutOfStock] = useState(true);
  const [madeInUSAOnly, setMadeInUSAOnly] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    CatalogController.list({ currency }).then((res) => {
      if (cancelled) return;
      setItems(res?.items ?? res ?? []);
    }).catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currency]);

  const facets = useMemo(() => deriveFacets(items, currency), [items, currency]);

  const filtered = useMemo(() => applyFilters(items, filters, currency), [items, filters, currency]);
  // "Include out of stock" toggle — when off, hide items where every
  // variant × warehouse pair is out (== 0). Cheap: stops early once any
  // variant has stock anywhere.
  const stockFiltered = useMemo(() => {
    if (includeOutOfStock) return filtered;
    return filtered.filter((it) => {
      const colors = it.colors?.length ? it.colors : [{ id: "default" }];
      const sizes = it.sizes?.length ? it.sizes : [null];
      for (const color of colors) {
        for (const size of sizes) {
          const sku = variantSku(it, color, size);
          const a = mockAvailability(sku, "b2b");
          for (const w of (a?.warehouses || [])) {
            if ((w.available || 0) > 0) return true;
          }
        }
      }
      return false;
    });
  }, [filtered, includeOutOfStock]);
  // "Made in USA" matches `origin.label` containing "USA" or item.madeInUSA flag.
  const usaFiltered = useMemo(() => {
    if (!madeInUSAOnly) return stockFiltered;
    return stockFiltered.filter((it) =>
      it.madeInUSA === true ||
      /\bUSA\b/i.test(it.origin?.label || "") ||
      /United States/i.test(it.origin?.label || "")
    );
  }, [stockFiltered, madeInUSAOnly]);
  const sorted = useMemo(() => applySort(usaFiltered, sort, currency), [usaFiltered, sort, currency]);
  const visible = useMemo(() => {
    if (!q.trim()) return sorted;
    return searchCatalog(sorted, q, { limit: 200 });
  }, [sorted, q]);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.crumb}>
          <Link to="/items" className={styles.back}>← Back to catalog</Link>
        </div>
        <h1 className={styles.title}>Order Sheet</h1>
        <p className={styles.lede}>
          Set quantities for each color across both warehouses, then review your order.
        </p>
        <div className={styles.searchRow}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Quick search by name, SKU, use…"
            className={styles.search}
            inputMode="search"
            autoComplete="off"
            aria-label="Search items"
          />
        </div>
        <div className={styles.quickFilters} role="group" aria-label="Quick filters">
          <button
            type="button"
            onClick={() => setMadeInUSAOnly((v) => !v)}
            aria-pressed={madeInUSAOnly}
            className={[styles.usaToggle, madeInUSAOnly ? styles.usaToggleActive : ""].join(" ")}
          >
            <span className={styles.usaFlag} aria-hidden>🇺🇸</span>
            <span className={styles.usaLabel}>Made in USA</span>
            <span className={styles.usaState}>{madeInUSAOnly ? "On" : "Off"}</span>
          </button>
          <button
            type="button"
            onClick={() => setIncludeOutOfStock((v) => !v)}
            aria-pressed={!includeOutOfStock}
            className={[styles.usaToggle, !includeOutOfStock ? styles.usaToggleActive : ""].join(" ")}
          >
            <span className={styles.usaLabel}>In stock only</span>
            <span className={styles.usaState}>{!includeOutOfStock ? "On" : "Off"}</span>
          </button>
        </div>
        <SortFilterBar
          count={visible.length}
          pageSize={visible.length || 1}
          onPageSize={() => {}}
          pageSizes={[visible.length || 1]}
          sort={sort}
          onSort={setSort}
          sortOptions={SORT_OPTIONS}
          filtersNode={
            <div>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={includeOutOfStock}
                  onChange={(e) => setIncludeOutOfStock(e.target.checked)}
                />
                <span>Include out of stock</span>
              </label>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={madeInUSAOnly}
                  onChange={(e) => setMadeInUSAOnly(e.target.checked)}
                />
                <span>
                  <span className={styles.toggleFlag} aria-hidden>🇺🇸</span>
                  Made in USA only
                </span>
              </label>
              <ItemFilters
                facets={facets}
                value={filters}
                onChange={setFilters}
                onClear={() => setFilters({})}
              />
            </div>
          }
          onClearFilters={() => {
            setFilters({});
            setIncludeOutOfStock(true);
            setMadeInUSAOnly(false);
          }}
        />
      </header>

      <div className={styles.grid}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : visible.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
            No items match.
          </Box>
        ) : (
          <>
            <div className={styles.legend} aria-hidden>
              <div className={styles.legendItem}>Item</div>
              <div className={styles.legendTotal}>Total</div>
              {WAREHOUSES.map((w) => (
                <div key={w.id} className={styles.legendCol}>
                  <span>{w.flag}</span> {w.label}
                </div>
              ))}
            </div>
            {visible.map((it) => (
              <OrderSheetItem
                key={it.id ?? it.sku}
                item={it}
                user={user}
                effectiveMode={effectiveMode}
              />
            ))}
          </>
        )}
      </div>

      <DraftBar
        totalQty={totalQty}
        totalAmount={totalAmount}
        onReview={() => nav("/order-review")}
        onClear={clear}
        hasLines={lines.length > 0}
      />
    </div>
  );
}

function DraftBar({ totalQty, totalAmount, onReview, onClear, hasLines }) {
  const { formatAmount } = useCurrency();
  return (
    <div className={styles.draftBar} role="region" aria-label="Order summary">
      <div className={styles.draftSummary}>
        <div>
          <div className={styles.draftLabel}>In your draft</div>
          <div className={styles.draftQty}>{totalQty} unit{totalQty === 1 ? "" : "s"}</div>
        </div>
        <div className={styles.draftRight}>
          <div className={styles.draftLabel}>Subtotal</div>
          <div className={styles.draftTotal}>{formatAmount(totalAmount)}</div>
        </div>
      </div>
      <div className={styles.draftActions}>
        {hasLines && (
          <button type="button" onClick={onClear} className={styles.draftClear}>
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={onReview}
          className={styles.draftReview}
          disabled={!hasLines}
        >
          Review order →
        </button>
      </div>
    </div>
  );
}
